import {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  SortingState,
} from "@tanstack/react-table";
import { ACTION_COLUMN_DEF_CONSTANTS } from "../ActionCell";
import { flatMap, uniq, uniqBy } from "lodash";
import { GenerateTableState } from "../Tables/hooks";
import {
  QueryDescription,
  SyntheticFieldDescription,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import { QueryFileTableResult } from "../../DocumentUploads/__queries__/table";
import { useHistory, useLocation } from "react-router";
import {
  ParcelImportConfiguration,
  SavedView,
  SubmissionTypeVersion,
} from "../../../generated/graphql";
import { walkSchema } from "common/services/formBuilderService";
import { buildSubmissionFieldAlias } from "common/utils/submissions";
import {
  SUBMISSION_CATEGORY,
  SUBMISSION_CATEGORY_TO_TABLE,
} from "common/constants";
import { getParcelColumnNamesForSavedView } from "../../Settings/DataImports/Parcels/ParcelImportWizard/utils";

export const sanityCheckLocalColumnOrder = ({
  localColumnOrder,
  defaultColumnIds,
}: {
  localColumnOrder: Maybe<ColumnOrderState>;
  defaultColumnIds: Array<string>;
}): Maybe<ColumnOrderState> => {
  if (!localColumnOrder) {
    return null;
  }

  if (localColumnOrder.some(columnId => !defaultColumnIds.includes(columnId))) {
    return defaultColumnIds;
  }

  //re-insert hidden column ids before action column on refresh
  if (localColumnOrder.length < defaultColumnIds.length) {
    const missingColumnIds = defaultColumnIds.filter(
      defaultColumnId => !localColumnOrder.includes(defaultColumnId!)
    );

    return [
      ...localColumnOrder.filter(
        columnId => columnId !== ACTION_COLUMN_DEF_CONSTANTS.id
      ),
      ...missingColumnIds,
      ACTION_COLUMN_DEF_CONSTANTS.id,
    ];
  }

  if (
    localColumnOrder[localColumnOrder.length - 1] !==
    ACTION_COLUMN_DEF_CONSTANTS.id
  ) {
    return [
      ...localColumnOrder.filter(
        columnId => columnId !== ACTION_COLUMN_DEF_CONSTANTS.id
      ),
      ACTION_COLUMN_DEF_CONSTANTS.id,
    ];
  }

  return null;
};

export const sanityCheckColumnSizing = <T,>({
  columnSizing,
  columns,
}: {
  columnSizing: ColumnSizingState;
  columns: ColumnDef<T>[];
}) => {
  const possibleColumnIds = columns.map(column => column.id!);
  if (
    Object.keys(columnSizing).some(
      columnId => !possibleColumnIds.includes(columnId)
    )
  ) {
    return {};
  }

  return null;
};

export const sanityCheckSorting = <T,>({
  sorting,
  columns,
}: {
  sorting?: SortingState;
  columns: ColumnDef<T>[];
}) => {
  const possibleColumnIds = columns.map(column => column.id!);

  if (
    sorting?.some(
      sortDefinition => !possibleColumnIds.includes(sortDefinition.id)
    )
  ) {
    return [];
  }

  return null;
};

export const fieldId = (field: { table: string; name: string }) =>
  `${field.table}.${field.name}`;

export const mergeFieldGroups = (
  fields: Array<{ name: string; table: string }>,
  requiredFields: Readonly<Array<{ name: string; table: string }>>
) =>
  uniqBy(
    [...fields, ...requiredFields],
    ({ table, name }) => `${table}-${name}`
  );

export const initializeColumns = ({
  initialTableState,
  tanstackColumnDefinitions,
  initialQueryDescription,
  actionsColumn,
}: {
  initialTableState: GenerateTableState;
  tanstackColumnDefinitions: Array<ColumnDef<any>>;
  initialQueryDescription: QueryDescription<SyntheticFieldDescription[]>;
  actionsColumn: ColumnDef<any>;
}) => {
  const tableStateToColumnDefinitions = (
    tableState:
      | GenerateTableState
      | QueryDescription<SyntheticFieldDescription[]>
  ) => {
    return tableState.fields.reduce((columns, field) => {
      const columnDefinition = tanstackColumnDefinitions.find(
        column => column.id === fieldId(field)
      );
      if (columnDefinition) {
        columns.push(columnDefinition);
      }
      return columns;
    }, [] as ColumnDef<QueryFileTableResult>[]);
  };

  let initialColumns = tableStateToColumnDefinitions(initialTableState);

  //if there are missing column definitions the table url is invalid and
  //we should revert to the default column definitions
  if (initialColumns.length !== initialTableState.fields.length) {
    initialColumns = tableStateToColumnDefinitions(initialQueryDescription);
  }

  //we always want to include the action column last
  initialColumns.push(actionsColumn);

  return initialColumns;
};

export type MinimalColumnDef<
  Fields extends Readonly<Array<{ name: string; table: string }>>
> = ColumnDef<{
  [Field in Fields[number] as `${Field["table"]}.${Field["name"]}`]: any;
}>;

export const useUpdateSavedView = () => {
  const { push } = useHistory();
  const { pathname, search: queryString } = useLocation();
  const searchParams = new URLSearchParams(queryString);

  return {
    updateSavedView: (viewId: string) => {
      searchParams.set("view", viewId);
      push(`${pathname}?${searchParams.toString()}`);
    },
  };
};

export const determineValidVisibility = ({
  savedView,
  queryDescription,
  parcelImportConfig,
  submissionTypes,
}: {
  savedView?: Pick<SavedView, "hiddenFromPublic">;
  queryDescription?: QueryDescription<SyntheticFieldDescription[]>;
  parcelImportConfig: Pick<ParcelImportConfiguration, "fieldNameOverrides">;
  submissionTypes: Array<{
    id: string;
    name: string;
    category: SUBMISSION_CATEGORY;
    currentVersion: Pick<SubmissionTypeVersion, "formStructure">;
  }>;
}) => {
  if (!savedView || savedView.hiddenFromPublic) {
    return true;
  }

  const parcelFields = getQueryFieldsByTable({
    queryDescription,
    tableNames: [TABLE_NAMES.PARCELS],
  });

  const mappedOverrides = parcelImportConfig.fieldNameOverrides.map(
    override => ({
      ...override,
      name: getParcelColumnNamesForSavedView(override.name),
    })
  );

  for (const field of parcelFields) {
    const fieldOverride = mappedOverrides.find(
      override => override.name === field
    );

    // Some fields, such as city, are always public and don't have a field override
    if (fieldOverride && !fieldOverride.isPublic) {
      return false;
    }
  }

  const submissionTableNames = [
    TABLE_NAMES.SUBMISSIONS,
    TABLE_NAMES.INSPECTIONS,
    TABLE_NAMES.PERMITS,
  ];
  // We can filter out values that don't start with a submission type id,
  // since those values (such as createdAt) aren't impacted by form visibility
  const submissionFields = getQueryFieldsByTable({
    queryDescription,
    tableNames: submissionTableNames,
  }).filter(value => value.match(/[0-9a-f]{8}_/));

  const visibleSubmissionFields = flatMap(submissionTypes, submissionType =>
    walkSchema({
      formStructure: submissionType.currentVersion.formStructure,
      visit: ({ uiSchema, path, indices, type }) => {
        if (!uiSchema?.["ui:hiddenFromPublic"] && !type.includes("object")) {
          const mappedName = buildSubmissionFieldAlias({
            submissionsTable:
              SUBMISSION_CATEGORY_TO_TABLE[submissionType.category],
            keys: [submissionType.name, ...path.split(".")],
            indices,
            submissionTypeId: submissionType.id,
          });

          return [mappedName];
        }

        return [];
      },
      merge: (acc, result) => acc.concat(result),
      halt: ({ type, uiSchema }) =>
        type.includes("object") && uiSchema?.["ui:hiddenFromPublic"],
    })
  );

  return submissionFields.every(value =>
    visibleSubmissionFields.includes(value)
  );
};

const getQueryFieldsByTable = ({
  queryDescription,
  tableNames,
}: {
  queryDescription?: QueryDescription<SyntheticFieldDescription[]>;
  tableNames: Array<TABLE_NAMES>;
}) => {
  const filteredFields =
    queryDescription?.fields.filter(field =>
      tableNames.includes(field.table as TABLE_NAMES)
    ) ?? [];

  const filteredFilters =
    queryDescription?.filters?.filter(filter =>
      tableNames.includes(filter.table as TABLE_NAMES)
    ) ?? [];

  return uniq([
    ...filteredFields.map(field => field.name),
    ...filteredFilters.map(filter => filter.field),
  ]);
};
